import { StoreType } from '@tock/shared/utils/connect';
import { createSelector } from 'reselect';

import { Filter } from 'state/calendarFilter';
import { ViewMode } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { activeTicketsByIdSelector } from 'selectors/experienceSelectors';
import { core } from '@tock/coretypes';
import { featuresSelector } from './featuresSelector';
import { isEmpty, toString } from 'es-toolkit/compat';

export type SelectedExperience = { id: number; name: string };

export const calendarFilterQueryParamsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams,
  (queryParams: Filter) => queryParams
);

export const calendarFilterSelectedExperienceIds = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.ticketTypeId,
  (ticketTypeId: number[]) => ticketTypeId
);

export const selectedPartyListServicesSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.service,
  (selectedPartyListServices): number[] => selectedPartyListServices
);

/* istanbul ignore next */
export const selectedPartyListSizesSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.size,
  (selectedPartyListSizes): number[] => selectedPartyListSizes
);

export const cappedSelectedPartyListSizesSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.size,
  (selectedPartyListSizes): string[] => selectedPartyListSizes.map((size) => toString(size))
);

/* istanbul ignore next */
export const selectedPartyListStatusSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.partyStatuses,
  (selectedPartyListStatus): string[] => selectedPartyListStatus
);

/* istanbul ignore next */
export const selectedPartyListExclusivesSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.exclusives,
  (selectedPartyListExclusives): core.enums.Exclusivity[] => selectedPartyListExclusives
);

export const walkInTicketTypePlaceholder = { id: -1, name: 'Walk-in' };

/* istanbul ignore next */
export const selectedPartyListExperiencesSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.ticketTypeId,
  activeTicketsByIdSelector,
  (
    selectedPartyListExperiences: number[],
    activeTicketsById: Dictionary<core.TicketType>
  ): SelectedExperience[] =>
    selectedPartyListExperiences.reduce<SelectedExperience[]>((selections, ticketTypeId) => {
      if (ticketTypeId === walkInTicketTypePlaceholder.id) {
        selections.push({ id: ticketTypeId, name: walkInTicketTypePlaceholder.name });
      }
      if (activeTicketsById[ticketTypeId]) {
        selections.push({ id: ticketTypeId, name: activeTicketsById[ticketTypeId].name });
      }
      return selections;
    }, [])
);
/* istanbul ignore next */

export const selectedPartyListSeatingOptionsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.seatingOptionsEnum,
  (seatingOptionsEnums): string[] => seatingOptionsEnums
);

/* istanbul ignore next */
export const selectedPartyListGuestTagsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.guestTag,
  (selectedPartyListGuestTags): string[] => selectedPartyListGuestTags
);

/* istanbul ignore next */
export const selectedPartyListVisitTagsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.visitTag,
  (selectedPartyListVisitTags): string[] => selectedPartyListVisitTags
);

/* istanbul ignore next */
export const selectedPartyListSupplementsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.ticketOptionId,
  (selectedPartyListSupplements: number[]): number[] => selectedPartyListSupplements
);

/* istanbul ignore next */
export const selectedPartyListEditedStatusSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.editedStatus,
  (editedStatus: 'YES' | 'NO' | 'ALL'): 'YES' | 'NO' | 'ALL' => editedStatus
);

export const selectedPartyListFilterUnansweredSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.isUnansweredFilterOn,
  (isUnansweredFilterOn: boolean) => {
    return { showUnansweredQuestions: isUnansweredFilterOn };
  }
);

export const selectedPartyListFilterUnfiredItemsSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.isUnfiredFilterOn,
  (isUnfiredFilterOn: boolean) => {
    return { showUnfiredItems: isUnfiredFilterOn };
  }
);

export const selectedPartyListFilterAmountDueSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.isAmountDueFilterOn,
  (isAmountDueFilterOn: boolean) => {
    return { showAmountDue: isAmountDueFilterOn };
  }
);

export const selectedPurchaseStatusSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.purchaseState,
  (purchaseStatus): core.TicketGroup.PurchaseState[] => purchaseStatus
);

export const selectedPurchaseStatusAndSeatingOptionsEnumSelector = createSelector(
  (state: StoreType) => state.calendarFilter.queryParams.purchaseState,
  selectedPartyListSeatingOptionsSelector,
  (purchaseStatus, seatingOptionsEnums): [core.TicketGroup.PurchaseState[], string[]] => [
    purchaseStatus,
    seatingOptionsEnums,
  ]
);

export const selectedCalendarFilterCountSelector = createSelector(
  featuresSelector,
  selectedPartyListEditedStatusSelector,
  selectedPartyListExperiencesSelector,
  selectedPartyListGuestTagsSelector,
  selectedPartyListVisitTagsSelector,
  selectedPartyListSizesSelector,
  selectedPartyListSupplementsSelector,
  selectedPartyListStatusSelector,
  selectedPartyListServicesSelector,
  selectedPurchaseStatusAndSeatingOptionsEnumSelector, // combined due to max number of selectors reached
  (state: StoreType) => state.calendarFilter.queryParams.isUnansweredFilterOn,
  (_state: StoreType, viewMode: ViewMode | undefined | null) => viewMode,
  (
    features,
    editedStatus,
    experiences,
    guestTags,
    visitTags,
    partySizes,
    supplements,
    partyStatus,
    services,
    purchaseStatusAndSeatingOptionsEnums,
    isUnanswered,
    viewMode
  ): number => {
    const purchaseStatus = isEmpty(purchaseStatusAndSeatingOptionsEnums)
      ? []
      : purchaseStatusAndSeatingOptionsEnums[0];
    const seatingOptionsEnums =
      isEmpty(purchaseStatusAndSeatingOptionsEnums) ||
      purchaseStatusAndSeatingOptionsEnums.length < 2
        ? []
        : purchaseStatusAndSeatingOptionsEnums[1];
    const isCalendarActive = viewMode && viewMode.mode === 'calendar';
    const filterSelections = [
      seatingOptionsEnums,
      experiences,
      guestTags,
      visitTags,
      partySizes,
      supplements,
      partyStatus,
      services,
      ...(isUnanswered ? [[1]] : [[]]),
      ...(isCalendarActive && editedStatus !== 'ALL' ? [[1]] : [[]]),
      ...(isCalendarActive ? [purchaseStatus] : [[]]),
    ];

    return filterSelections.reduce((total, selectionArray) => total + selectionArray.length, 0);
  }
);
